/**
 * Single source of truth for the Jackpot vocabulary. Components branch on
 * these, never on raw strings. Same idiom as `tournamentConstants.js`.
 */

export const JACKPOT_VIEW_TYPE = {
  ACTIVE: 'ACTIVE',
  HISTORY: 'HISTORY',
}

// Top-level JackpotMenu tabs.
export const JACKPOT_TAB = {
  ACTIVE: { KEY: 'ACTIVE', LABEL: 'Active Jackpots', VIEW_TYPE: JACKPOT_VIEW_TYPE.ACTIVE },
  HISTORY: { KEY: 'HISTORY', LABEL: 'Jackpots History', VIEW_TYPE: JACKPOT_VIEW_TYPE.HISTORY },
}

// JackpotOverviewTabs sub-tabs, keyed by view type (Active/History show different tabs).
export const JACKPOT_OVERVIEW_TAB = {
  [JACKPOT_VIEW_TYPE.ACTIVE]: [
    { KEY: 'CONTRIBUTIONS', LABEL: 'Contributions' },
    { KEY: 'PRIZES', LABEL: 'Prizes' },
    { KEY: 'RULES', LABEL: 'Rules' },
  ],
  [JACKPOT_VIEW_TYPE.HISTORY]: [
    { KEY: 'CONTRIBUTIONS', LABEL: 'My Contributions' },
    { KEY: 'RESULTS', LABEL: 'Results' },
    { KEY: 'RULES', LABEL: 'Rules' },
  ],
}

// JackpotContributionsTab's own pill sub-tabs, keyed by view type. History has
// only one — the tab pill bar hides itself when there's nothing to switch between.
export const JACKPOT_CONTRIBUTIONS_TAB = {
  [JACKPOT_VIEW_TYPE.ACTIVE]: [
    { KEY: 'TOP', LABEL: 'Top Contributions' },
    { KEY: 'MY', LABEL: 'My Contributions' },
  ],
  [JACKPOT_VIEW_TYPE.HISTORY]: [{ KEY: 'MY', LABEL: 'My Contributions' }],
}

export const JACKPOT_TABLE_HEADERS = {
  TOP_CONTRIBUTIONS: [
    { key: 'userId', label: 'Player ID' },
    { key: 'winChance', label: 'Win Chance' },
    { key: 'tickets', label: 'Tickets Allocated' },
  ],
  MY_CONTRIBUTIONS: [
    { key: 'dateTime', label: 'Date & Time' },
    { key: 'type', label: 'Contribution Type' },
    { key: 'stake', label: 'Stake', showCurrency: true },
    { key: 'contribution', label: 'Contribution', showCurrency: true },
    { key: 'tickets', label: 'Tickets Allocated' },
  ],
  PRIZES: [
    { key: 'position', label: 'Position' },
    { key: 'reward', label: 'Rewards', showCurrency: true },
  ],
  RESULTS: [
    { key: 'position', label: 'Position' },
    { key: 'userId', label: 'Player ID' },
    { key: 'winningTicket', label: 'Winning Ticket Number' },
    { key: 'reward', label: 'Reward', showCurrency: true },
  ],
}

// What the JackpotDetail top summary shows, per view type — adapted to what
// our GraphQL contract actually provides (defenderV2's REST equivalent shows
// a "My Contribution %" stat for history sourced from a field our schema
// doesn't expose; that stat is intentionally dropped rather than faked, see
// [[jackpot-architecture]] memory).
export const JACKPOT_DETAIL_DISPLAY = {
  [JACKPOT_VIEW_TYPE.ACTIVE]: {
    showEndedDate: false,
    showFilledBar: true,
    showMaturityAmount: true,
    showWinChance: true,
    showTotalReward: false,
  },
  [JACKPOT_VIEW_TYPE.HISTORY]: {
    showEndedDate: true,
    showFilledBar: false,
    showMaturityAmount: true,
    showWinChance: false,
    showTotalReward: true,
  },
}

// Shown in the header bar's tooltip area and the main-layout message strip
// (Index.jsx) — ported from defenderV2's JACKPOT_RATIO["1"].
export const JACKPOT_BET_RATIO_MESSAGE = '1% of bet amount goes to jackpot'

export const JACKPOT_MESSAGES = {
  NO_ACTIVE_JACKPOT: 'No Active Jackpot At This Time',
  NO_HISTORY: 'No Jackpot History Found',
  NO_DETAILS: 'No Jackpot details available.',
  NO_TICKETS: 'No tickets available for this jackpot.',
  NO_TOP_CONTRIBUTIONS: 'No Contribution available.',
  NO_MY_CONTRIBUTIONS: 'No Contribution available.',
  NO_PRIZES: 'No Prize details available.',
  NO_RESULTS: 'No Results available.',
  NO_BET_TICKETS: 'No Tickets available.',
  BET_TO_JOIN: 'Bet To Join The Jackpot',
  NOT_PARTICIPATED: 'You have not participated in this jackpot.',
}

export const JACKPOT_TEXTS = {
  JACKPOT_VALUE_PREFIX: 'Jackpot Value :',
  ENDED_DATE_PREFIX: 'Ended :',
  MY_CONTRIBUTION_PREFIX: 'My Contribution :',
  MY_WIN_CHANCE_PREFIX: 'My Win Chance :',
  TOTAL_REWARDS_PREFIX: 'My Reward :',
  TOTAL_TICKETS_PREFIX: 'Total Jackpot Tickets :',
}

// Ported verbatim from defenderV2's RULES_LIST — `{currencyCode}` is replaced
// by the caller (see core/jackpot/jackpotView.js).
export const JACKPOT_RULES_TITLE = 'How it Works'
export const JACKPOT_RULES_LIST = [
  'For every bet, 1% of bet amount goes to jackpot.',
  'For classic bet, you will receive 1 jackpot ticket for every {currencyCode}0.10 Stake Amount.',
  'For Free bet (package 1), you will receive 1 jackpot ticket for every {currencyCode}0.08 Stake Amount.',
  'For Free bet (package 2), you will receive 1 jackpot ticket for every {currencyCode}0.06 Stake Amount.',
  'For Free bet (package 3), you will receive 1 jackpot ticket for every {currencyCode}0.04 Stake Amount.',
  'Once the jackpot is full, wining tickets will be selected and prizes will be given.',
]
